import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Button — refresh 2026-06 (editorial reto, achatado).
 *
 * - Radius reto (rounded-xl → ~4px via token) — geométrico, sem pílula
 * - Sem efeito de flutuar (hover-lift removido) nem sombras coloridas grandes
 * - Hover = mudança sutil de cor/borda; feedback tátil leve no :active
 * - Touch target ≥44px no mobile
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-xl text-sm font-semibold",
    "transition-colors duration-150 ease-out",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
    "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "min-h-[44px]",
    "active:opacity-90",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primário: verde sólido, hover escurece (sem glow/lift)
        default:
          "bg-primary text-primary-foreground hover:bg-[rgb(var(--green-deep))]",
        // Secundário: superfície com borda fina, hover realça a borda
        secondary:
          "bg-card text-foreground border border-border hover:border-[rgb(var(--ink))]/30 hover:bg-secondary",
        // Outline: alias semântico
        outline:
          "bg-card text-foreground border border-border hover:border-[rgb(var(--ink))]/30 hover:bg-secondary",
        // Destrutivo
        destructive:
          "bg-destructive text-destructive-foreground hover:opacity-95",
        // Ghost: invisível até hover
        ghost:
          "bg-transparent text-foreground hover:bg-secondary",
        // Link: underline com offset
        link:
          "h-auto min-h-0 px-0 text-primary underline underline-offset-[3px] decoration-1 hover:decoration-2",
      },
      size: {
        default: "h-11 px-5 has-[>svg]:px-4",
        xs: "h-7 min-h-0 px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5 rounded-lg",
        sm: "h-9 min-h-0 px-3.5 text-sm has-[>svg]:px-3 rounded-lg",
        lg: "h-12 px-6 text-[15px] has-[>svg]:px-5",
        icon: "size-11",
        "icon-xs": "size-7 min-h-0 [&_svg:not([class*='size-'])]:size-3.5 rounded-lg",
        "icon-sm": "size-9 min-h-0 rounded-lg",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
